import { h, Component, State, Prop, Watch, Listen, Event, EventEmitter } from '@stencil/core';
import { fetchComponentData } from '../../utils/api-utils';
import { FooterApiResponse } from '../../types/entitites';
import { extractSvg } from '../../utils/svg-utils';
import { getApiEndpoint } from '../../constants';
import i18n from '../../utils/i18n';
import { getCookieLanguage, setCookieLanguage } from '../../utils/cookie-utils';

@Component({
  tag: 'ps-footer',
  styleUrl: 'footer.styles.scss',
})
export class Footer {
  @Prop() domain = 'patentstyret.no';
  @Prop() env?: 'prod' | 'dev' = 'prod';
  @Prop() hash: string;
  @Prop() ttl: number = 300;
  @Prop({ mutable: true, reflect: true }) language?: 'no' | 'en';
  @Prop() languageRouting: boolean = false;

  @State() fetchResult: FooterApiResponse;

  @Event({ bubbles: true, composed: true }) psOnLanguageChanged: EventEmitter<string>;

  @Watch('language')
  onLanguageValueChanged() {
    this.fetchData(true);
  }

  // Listen on event from other web-components
  @Listen('psOnLanguageChanged', { target: 'body' })
  onLanguageChangedEvent(event: CustomEvent) {
    if (event.detail && this.language !== event.detail) {
      this.language = event.detail === 'en' ? 'en' : 'no';
    }
  }

  getFooterEndPoint = () => {
    return `${getApiEndpoint(this.env, this.language)}?footer=True`;
  };

  async fetchData(hardReload: boolean = false) {
    const ttl = hardReload ? 0 : this.ttl;
    const url = this.getFooterEndPoint();
    const key = this.hash ? this.hash + '-patentstyret-stenciljs-footer-component' : 'patentstyret-stenciljs-footer-component';
    this.fetchResult = await fetchComponentData(url, key, ttl);
  }

  handleToggleLanguageClick = (e: MouseEvent) => {
    const lang = this.language === 'no' ? 'en' : 'no';
    setCookieLanguage(lang, this.domain);
    if (!this.languageRouting) {
      this.language = lang;
      i18n.changeLanguage(lang);
      this.psOnLanguageChanged.emit(lang);
      e.preventDefault();
    }
  };

  initLanguage() {
    this.language = this.language || getCookieLanguage() || (i18n.language as 'no' | 'en');
    const overwriteBrowserLang = !!this.language && this.language != i18n.language;
    if (overwriteBrowserLang) {
      if (this.language === 'no' || this.language === 'en') {
        i18n.changeLanguage(this.language); // update language
        setCookieLanguage(this.language, this.domain); // set local-storage value
        this.psOnLanguageChanged.emit(this.language); // emit value to parent/consumer app
      }
    }
  }

  componentWillLoad() {
    this.initLanguage();
  }

  componentDidRender() {
    extractSvg();
  }

  // Fetch data when the component is loaded (updated with @Watch language)
  // componentDidLoad() {
  //   this.fetchData();
  // }

  render() {
    if (!this.fetchResult || Object.keys(this.fetchResult).length === 0) {
      return;
    }

    const { accessibility, assets, siteLink, footer, languageLink } = this.fetchResult;

    return (
      <footer class="cms-reset cms-global cms-typography main-footer">
        <div class="container">
          <div class="row">
            <div class="col-12 main-footer__logo">
              <a href={siteLink.href} aria-label={accessibility.logoLinkText}>
                {footer.logo && <img src={footer.logo.link} alt={footer.logo.alt} />}
              </a>
            </div>
            <div class="col-sm-6 main-footer__links main-footer__links--left">
              <ul class="links">
                {footer.leftLinks.map((leftLink: { url: { target: string; href: string }; title: any }) => {
                  return (
                    <li>
                      <a target={leftLink.url.target} href={leftLink.url.href} class={`btn btn--tertiary btn--special ${leftLink.url.target == '_self' ? 'btn--internal' : ''}`}>
                        <span>{leftLink.title}</span>
                        {leftLink.url.target == '_self' && <span class="svg-icon" innerHTML={assets.images.arrowRight}></span>}
                        {leftLink.url.target == '_blank' && <span class="svg-icon" innerHTML={assets.images.external}></span>}
                      </a>
                    </li>
                  );
                })}
              </ul>
              <ul class="social">
                {footer.someLinks.map((someLink: { url: { target: string; href: string }; icon: { alt: string; link: string; title: string } }) => {
                  return (
                    <li>
                      <a target={someLink.url.target} href={someLink.url.href} aria-label={`${accessibility.gotToPat} ${someLink.icon.title}`}>
                        <img aria-hidden="true" src={someLink.icon.link} alt={someLink.icon.alt} />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div class="col-sm-6 main-footer__links main-footer__links--right">
              <ul class="links">
                {footer.rightLinks.map((rightLink: { url: { target: string; href: string }; title: any }) => {
                  return (
                    <li>
                      <a target={rightLink.url.target} href={rightLink.url.href} class="btn btn--tertiary btn--special">
                        <span>{rightLink.title}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div class="col-md-12 main-footer__links main-footer__links--bottom">
              <ul class="links">
                {languageLink && (
                  <li>
                    <a target="_self" href={languageLink.url.href} class="btn btn--tertiary btn--special" onClick={this.handleToggleLanguageClick}>
                      <span>{languageLink.title}</span>
                      <span class="svg-icon" innerHTML={assets.images.language}></span>
                    </a>
                  </li>
                )}
                {footer.bottomLinks.map((bottomLink: { url: { target: string; href: string }; title: any }) => {
                  return (
                    <li>
                      <a target={bottomLink.url.target} href={bottomLink.url.href} class="btn btn--tertiary btn--special">
                        <span>{bottomLink.title}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
              <p class="main-footer__copyright">© Patentstyret</p>
            </div>
          </div>
        </div>
      </footer>
    );
  }
}
